import { useState, useEffect, useCallback } from 'react';

import * as fp from 'lodash/fp';

import { AlgoliaItem, UniqItem } from '~/types/store/item';
import { FetchingDataStatus } from '~/types/utilities';

import { itemIndex } from '~/algolia/init';
import getItems from '~/firebase/get/getItems';
import isInOperatingTime from '~/utils/isInOperatingTime';

type FetchItem = Pick<AlgoliaItem, 'objectID' | 'storeId' | 'itemId' | 'preOrderSchedule'>;

export default function useRestItem(storeIds: Array<string>, pickMealAt: Date | null) {
  const [fetchingStatus, setFetchingStatus] = useState<FetchingDataStatus>(FetchingDataStatus.INIT);
  const [items, setItems] = useState<Array<UniqItem>>([]);
  const [nextPage, setNextPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);

  const handleGetItems = useCallback(
    async ({
      storeIds,
      init,
      nextPage,
    }: {
      storeIds: Array<string>;
      nextPage: number;
      init: boolean;
    }) => {
      if (!storeIds.length) {
        setFetchingStatus(FetchingDataStatus.INIT);
        setItems([]);
        setNextPage(0);
        setMaxPage(0);
        return;
      }
      try {
        setFetchingStatus(FetchingDataStatus.LOADING);

        const res = await itemIndex.search<FetchItem>('', {
          filters: `isAvailable:true${
            fp.gt(storeIds.length, 0)
              ? ` AND ( ${storeIds.map((id) => `storeId:${id}`).join(' OR ')} )`
              : ''
          }`,
          page: init ? 0 : nextPage,
          attributesToRetrieve: ['storeId', 'itemId', 'preOrderSchedule'],
          hitsPerPage: 50,
        });
        setNextPage(res.page + 1);
        setMaxPage(res.nbPages);
        const algoliaItems = res.hits
          .filter((hit) => hit && isInOperatingTime(hit.preOrderSchedule, pickMealAt))
          .map(({ storeId, itemId }) => ({ storeId, id: itemId }));
        const itemsFromFirestore = await getItems(algoliaItems);
        setItems((prev) => (init ? itemsFromFirestore : prev.concat(itemsFromFirestore)));
        setFetchingStatus(FetchingDataStatus.SUCCESS);
      } catch (err) {
        setNextPage(0);
        setItems([]);
        setFetchingStatus(FetchingDataStatus.FAIL);
        console.error('algolia get items error', err);
      }
    },
    [pickMealAt],
  );

  const handleLoadNextPage = () => {
    if (fetchingStatus === FetchingDataStatus.LOADING || nextPage > maxPage) {
      return;
    }
    handleGetItems({ storeIds, nextPage, init: false });
  };

  useEffect(() => {
    handleGetItems({ storeIds, nextPage: 0, init: true });
  }, [storeIds, pickMealAt, handleGetItems]);

  return { fetchingStatus, items, handleLoadNextPage };
}
